import { useState } from "react";

const ICONS = [
  "ri-ancient-pavilion-line", "ri-restaurant-line", "ri-gamepad-line",
  "ri-hotel-line", "ri-plant-line", "ri-eye-line", "ri-star-line",
  "ri-map-pin-line", "ri-building-line", "ri-compass-3-line",
];

interface CategoryFormData {
  title: string;
  desc: string;
  slug: string;
  icon: string;
  count: string;
  image?: string;
}

interface CategoryFormModalProps {
  mode: "add" | "edit";
  initial?: Partial<CategoryFormData>;
  onSave: (data: CategoryFormData) => void;
  onClose: () => void;
}

export default function CategoryFormModal({ mode, initial = {}, onSave, onClose }: CategoryFormModalProps) {
  const [form, setForm] = useState<CategoryFormData>({
    title: initial.title ?? "",
    desc: initial.desc ?? "",
    slug: initial.slug ?? "",
    icon: initial.icon ?? "ri-map-pin-line",
    count: initial.count ?? "0+ mekan",
    image: initial.image ?? "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim()) return;
    onSave(form);
  };

  const autoSlug = (title: string) =>
    title.toLowerCase()
      .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
      .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-bold text-stone-800">
            {mode === "add" ? "Yeni Kategori Ekle" : "Kategoriyi Düzenle"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Kategori Adı (TR) *</label>
            <input required type="text" value={form.title}
              onChange={(e) => {
                const title = e.target.value;
                setForm((f) => ({
                  ...f, title,
                  slug: mode === "add" ? autoSlug(title) : f.slug,
                }));
              }}
              placeholder="Örn: Tarihi Yerler"
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">URL Slug *</label>
            <input required type="text" value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              readOnly={mode === "edit"}
              placeholder="tarihi-yerler"
              className={`w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none transition-colors font-mono ${
                mode === "edit" ? "bg-stone-50 text-stone-400 cursor-not-allowed" : "focus:border-emerald-400"
              }`} />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Açıklama</label>
            <textarea rows={2} value={form.desc}
              onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
              placeholder="Kategori açıklaması..."
              maxLength={200}
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Mekan Sayısı Etiketi</label>
              <input type="text" value={form.count}
                onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                placeholder="120+ mekan"
                className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Seçilen İkon</label>
              <div className="flex items-center gap-2 px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50">
                <i className={`${form.icon} text-base text-emerald-700`} />
                <span className="text-xs text-stone-500 font-mono truncate">{form.icon}</span>
              </div>
            </div>
          </div>

          {/* Icon Picker */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-2">İkon Seç</label>
            <div className="flex flex-wrap gap-2">
              {ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, icon }))}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${
                    form.icon === icon
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-stone-200 text-stone-500 hover:border-stone-400"
                  }`}
                >
                  <i className={`${icon} text-base`} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Kapak Görseli URL</label>
            <input type="url" value={form.image}
              onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
              placeholder="https://..."
              className="w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
              İptal
            </button>
            <button type="submit"
              className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {mode === "add" ? "Kategori Ekle" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
